import React from 'react';
import moment from 'moment';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import DataTableHeader from '../../../components/DataTableHeader';
import { translate } from '../../../services/translate';
import DataTableContainer from '../../../components/DataTableContainer';
import MyDataTable from '../../../components/MyDataTable';
import Badge from '../../../components/Badge';
import MyAppText from '../../../components/MyAppText';

type SynchronizationsTabProps = {
    metadata?: CameraMetadata | null;
    isLoading: boolean;
};

type SynchronizationTableRow = {
    label: string;
    synchronized: boolean;
    date: string;
    properties: { label: string; value: string; }[];
};

export default function SynchronizationsTab({ metadata, isLoading }: SynchronizationsTabProps) {
    const { styles } = useStyles(styleSheet);
    let synchronizationItems: SynchronizationTableRow[] = [];

    if (metadata?.configurationLastSynchronization) {
        const data = metadata.configurationLastSynchronization.data;

        for (const key of Object.keys(data) as Array<keyof typeof data>) {
            const item = data[key];
            if (!item || key.startsWith('_')) {
                continue;
            }

            switch (item.type) {
                case 'log': {
                    synchronizationItems.push({
                        label: translate('cameraConfigurationSynchronization_' + key),
                        synchronized: !item.error,
                        date: moment(item.finishedAt).format('DD/MM/YYYY HH:mm:ss'),
                        properties: [{
                            label: translate('synchronizationStartDate'),
                            value: moment(item.startedAt).format('DD/MM/YYYY HH:mm:ss')
                        }, {
                            label: translate('synchronizationFinishDate'),
                            value: item.finishedAt ? moment(item.finishedAt).format('DD/MM/YYYY HH:mm:ss') : translate('notSynced')
                        }]
                    });
                    break;
                }
                case 'value': {
                    synchronizationItems.push({
                        label: translate('cameraConfigurationSynchronization_' + key),
                        synchronized: true,
                        date: moment(item.date).format('DD/MM/YYYY HH:mm:ss'),
                        properties: [{
                            label: translate('value'),
                            value: translateItemValue(item.value)
                        }]
                    });
                    break;
                }
            }
        }

        synchronizationItems = synchronizationItems.toSorted((a, b) => a.label.localeCompare(b.label));
    }

    function translateItemValue(value: string | number | boolean): string {
        if (typeof value === 'boolean') {
            return value ? translate('yes') : translate('no');
        }

        if (typeof value === 'number') {
            return value.toString();
        }

        return value;
    }

    function renderProperties(data: SynchronizationTableRow) {
        return (
            <View style={styles.propertiesTable}>
                <View style={[styles.propertiesRow, styles.propertiesHeaderRow]}>
                    <View style={[styles.propertiesCell, styles.propertiesLabelCell]}>
                        <MyAppText style={styles.propertiesHeaderText}>{translate('property')}</MyAppText>
                    </View>
                    <View style={[styles.propertiesCell, styles.propertiesValueCell]}>
                        <MyAppText style={styles.propertiesHeaderText}>{translate('value')}</MyAppText>
                    </View>
                </View>

                {data.properties.map((property, index) => (
                    <View
                        key={index}
                        style={[
                            styles.propertiesRow,
                            index === data.properties.length - 1 && styles.propertiesLastRow,
                        ]}
                    >
                        <View style={[styles.propertiesCell, styles.propertiesLabelCell]}>
                            <MyAppText style={styles.propertiesBodyText}>{property.label}</MyAppText>
                        </View>
                        <View style={[styles.propertiesCell, styles.propertiesValueCell]}>
                            <MyAppText style={styles.propertiesBodyText}>{property.value}</MyAppText>
                        </View>
                    </View>
                ))}
            </View>
        );
    }

    return (
        <>
            <DataTableHeader
                zIndex={2}
                title={translate('synchronizations')}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: translate('status'),
                        maxWidth: '200px',
                        cell: row =>
                            <View style={{ justifyContent: 'center', alignItems: 'flex-start' }}>
                                <Badge mode='SQUARE' color={row.synchronized ? 'green' : 'red'}>
                                    {row.synchronized ? translate('synced') : translate('notSynced')}
                                </Badge>
                            </View>
                        ,
                    }, {
                        name: translate('configuration'),
                        selector: row => row.label,
                        wrap: true,
                        maxWidth: '500px',
                    },
                    {
                        name: translate('lastSynchronizationDate'),
                        selector: row => row.date,
                        wrap: true,
                        maxWidth: '250px'
                    },]}
                    data={synchronizationItems}
                    progressPending={isLoading}
                    expandableRowsComponent={({ data }) => renderProperties(data)}
                    expandableRows={true}
                />}
            />
        </>
    );
}

const styleSheet = createStyleSheet(theme => ({
    propertiesTable: {
        overflow: 'hidden',
    },
    propertiesHeaderRow: {
        backgroundColor: theme.colors.dataTable.oddRowBackground,
    },
    propertiesRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.dataTable.border,
    },
    propertiesLastRow: {
        borderBottomWidth: 0,
    },
    propertiesCell: {
        paddingVertical: 10,
        paddingHorizontal: 12,
    },
    propertiesLabelCell: {
        flex: 1,
        borderRightWidth: 1,
        borderRightColor: theme.colors.dataTable.border,
        maxWidth: 750,
        paddingLeft: 64
    },
    propertiesValueCell: {
        flex: 1,
    },
    propertiesHeaderText: {
        fontSize: 14,
        fontWeight: '700',
    },
    propertiesBodyText: {
        fontSize: 14,
    },
}));
